import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import * as ConstructionProgressActions from 'src/app/modules/ngrx-store/construction-progress/construction-progress.actions';
import * as MessageActions from 'src/app/modules/ngrx-store/message/message.actions';
import * as RealtorActions from 'src/app/modules/ngrx-store/realtor/realtor.actions';
import * as ServiceRequestsActions from 'src/app/modules/ngrx-store/service-requests/service-requests.actions';
import * as YourHomeActions from 'src/app/modules/ngrx-store/your-home/your-home.actions';
import { getLastLoadedSalesAgreementId } from 'src/app/modules/ngrx-store/your-home/your-home.selectors';

/**
 * Centralized home reset coordinator
 *
 * Detects when a user switches Sales Agreements and coordinates
 * the reset of all home-dependent state across features. This prevents
 * ghost data and ensures a clean state transition.
 *
 * When adding new features with home-dependent state:
 * 1. Create a resetFeature action in the feature's actions file
 * 2. Add a reducer handler for the reset action
 * 3. Add the reset action to the resetAllHomeData$ effect below
 *
 * This pattern ensures all home-related data clears atomically when switching homes.
 */
@Injectable()
export class HomeResetEffects {
	constructor(
		private actions$: Actions,
		private store: Store
	) { }

	/**
	 * Detects SAG switches and coordinates reset of all home-dependent state.
	 * Dispatches reset actions for all features when the user navigates to a different home.
	 */
	resetAllHomeData$ = createEffect(() => this.actions$.pipe(
		ofType(YourHomeActions.loadHomeDetails),
		withLatestFrom(this.store.select(getLastLoadedSalesAgreementId)),
		map(([action, lastLoadedSag]) => ({
			newSag: action.salesAgreementId,
			lastLoadedSag
		})),
		filter(({ newSag, lastLoadedSag }) => newSag !== undefined && newSag !== lastLoadedSag),
		mergeMap(() => [
			RealtorActions.resetRealtorData(),
			MessageActions.resetMessageThreads(),
			ConstructionProgressActions.resetConstructionUpdates(),
			ServiceRequestsActions.resetServiceRequests(),
			// Add new feature resets here
		])
	));
}
